import { createApi } from '@reduxjs/toolkit/query/react';
import { QUERY } from '@constants/query.constants';
import {
  ICommunityConversationParams,
  IConversation,
  IConversationFansQueryParams,
  IConversationQueryParams,
  IConversationResponse,
  IConversationRestrictionOptions,
  IConversationSet,
  ICreateConversation,
  ICreateConversationResponse,
  INormalizedConversationResponse,
  INormalizedFansResponse,
  ITopicConversationParams,
} from '@ui/components';
import { authQuery } from '../config/query.config';
import { createEntityAdapter } from '@reduxjs/toolkit';
import { cookieService } from '@lib/cookie.service';
import {
  IFansOfConverstion,
  IReorderConversationsPayload,
} from '@ui/components/organisms/conversation';
import { buildModeratorRestrictionsPayload } from '@lib/utils/moderator-restrictions';
import {
  buildMarkCreatorConversationApiRequest,
  type MarkCreatorConversationActionRequest,
} from '@ui/analytics';

/** Returns the best available conversation activity time as a sortable timestamp. */
export const getConversationActivityTimestamp = (conversationSet: IConversationSet) => {
  const activityAt =
    (conversationSet as IConversationSet & { last_activity_at?: string }).last_activity_at ||
    conversationSet.last_message ||
    conversationSet.conversation?.updated_at ||
    conversationSet.conversation?.miscellaneous?.updated_at ||
    '';
  const timestamp = Date.parse(activityAt);

  return Number.isNaN(timestamp) ? 0 : timestamp;
};

/** Orders conversations by newest activity, then descending ID for deterministic ties. */
export const compareConversationActivity = (a: IConversationSet, b: IConversationSet) => {
  const activityDifference =
    getConversationActivityTimestamp(b) - getConversationActivityTimestamp(a);
  if (activityDifference !== 0) return activityDifference;
  return String(b.conversation.id).localeCompare(String(a.conversation.id));
};

export const conversationsAdapter = createEntityAdapter({
  selectId: (item: IConversationSet) => item.conversation.id,
  sortComparer: compareConversationActivity,
});
export const conversationFansAdapter = createEntityAdapter({
  selectId: (fan: IFansOfConverstion) => fan.id,
  sortComparer: (a, b) => a.user_name?.localeCompare(b.user_name) || 0,
});

export const topicAdapter = createEntityAdapter({
  selectId: (topic: IConversationSet) => topic.conversation.id,
  sortComparer: (a, b) => {
    const indexA = a.conversation.order_index ?? 0;
    const indexB = b.conversation.order_index ?? 0;

    if (indexA !== indexB) {
      return indexA - indexB;
    }

    const dateA = new Date(a.conversation.created_at).getTime();
    const dateB = new Date(b.conversation.created_at).getTime();

    return dateB - dateA;
  },
});

export const conversationFansSelector = conversationFansAdapter.getSelectors();

export const conversationsSelector = conversationsAdapter.getSelectors();
export const topicSelectors = topicAdapter.getSelectors();

const DEFAULT_CONVERSATION_TYPE = 'all';

/** Serializes conversations cache identity by endpoint, creator, and type while pagination shares one entry. */
export const serializeConversationsQueryArgs = ({
  endpointName,
  queryArgs,
}: {
  endpointName: string;
  queryArgs: IConversationQueryParams;
}): string =>
  `${endpointName}-${queryArgs.creator_id}-${
    queryArgs.conversation_type ?? DEFAULT_CONVERSATION_TYPE
  }`;

/** Keeps retained disabled access codes private until the settings update succeeds. */
const createSafeOptimisticConversationPatch = ({
  access_code_enabled: _accessCodeEnabled,
  ...optimisticPatch
}: Partial<IConversation>): Partial<IConversation> => optimisticPatch;

export const conversationsApi = createApi({
  reducerPath: 'conversations-api',
  baseQuery: authQuery,
  tagTypes: [
    QUERY.conversations,
    QUERY.singleConversation,
    QUERY.communityConversations,
    QUERY.conversationFans,
    QUERY.topics,
  ],
  endpoints: (builder) => ({
    getConversations: builder.query<INormalizedConversationResponse, IConversationQueryParams>({
      query: (params) => ({
        url: 'public/v1/forum/conversation',
        params,
      }),
      providesTags: [QUERY.conversations],
      transformResponse: ({ conversations, total_pages }: IConversationResponse) => {
        const initialState = conversationsAdapter.getInitialState();
        const filtered = conversations
          .filter((conversation) => conversation) // somehow null slips through
          .map((conversationSet) => ({
            ...conversationSet,
            conversation: {
              ...conversationSet.conversation,
              main_thread: conversationSet.conversation.main_thread || conversationSet.thread_id, // fallback thread id
            },
          }));

        return {
          conversations: conversationsAdapter.setAll(initialState, filtered),
          totalPages: total_pages,
        };
      },
      forceRefetch: ({ currentArg, previousArg }) => {
        const isSameCreator = currentArg.creator_id === previousArg?.creator_id;
        const isSameConversationType =
          currentArg.conversation_type === previousArg?.conversation_type;
        const isSamePage = currentArg.page === previousArg?.page;
        return !isSameCreator || !isSameConversationType || !isSamePage;
      },
      serializeQueryArgs: serializeConversationsQueryArgs,
      merge: (currentState, incomingState, { arg }) => {
        const incoming = conversationsSelector.selectAll(incomingState.conversations);
        currentState.conversations =
          arg.page === 1
            ? conversationsAdapter.setAll(currentState.conversations, incoming)
            : conversationsAdapter.upsertMany(currentState.conversations, incoming);
        currentState.totalPages = incomingState.totalPages;
      },
    }),

    getTopicConversations: builder.query<INormalizedConversationResponse, ITopicConversationParams>(
      {
        query: (params) => ({
          url: 'public/v1/forum/conversation',
          params,
        }),
        providesTags: [QUERY.topics, QUERY.conversations],
        transformResponse: ({ conversations, total_pages }: IConversationResponse) => {
          const initialState = topicAdapter.getInitialState();
          const filtered = conversations
            .filter((conversation) => conversation) // somehow null slips through
            .map((conversationSet) => ({
              ...conversationSet,
              conversation: {
                ...conversationSet.conversation,
                main_thread: conversationSet.conversation.main_thread || conversationSet.thread_id, // fallback thread id
              },
            }));

          return {
            conversations: topicAdapter.setAll(initialState, filtered),
            totalPages: total_pages,
          };
        },
        forceRefetch: ({ currentArg, previousArg }) => {
          const isSameCreator = currentArg.creator_id === previousArg?.creator_id;
          const isSamePage = currentArg.page === previousArg?.page;
          return !isSameCreator || !isSamePage;
        },
        serializeQueryArgs: ({ endpointName, queryArgs }) => {
          return `${endpointName}-${queryArgs?.creator_id}`;
        },
        merge: (currentState, incomingState, { arg }) => {
          const incoming = topicSelectors.selectAll(incomingState.conversations);
          currentState.conversations =
            arg.page === 1
              ? topicAdapter.setAll(currentState.conversations, incoming)
              : topicAdapter.upsertMany(currentState.conversations, incoming);
          currentState.totalPages = incomingState.totalPages;
        },
      },
    ),
    getCommunityConversations: builder.query<
      INormalizedConversationResponse,
      ICommunityConversationParams
    >({
      query: (params) => ({
        url: 'public/v1/forum/conversation',
        params,
      }),
      providesTags: [QUERY.communityConversations],
      transformResponse: ({ conversations, total_pages }: IConversationResponse, {}) => {
        const initialState = conversationsAdapter.getInitialState();
        const filtered = conversations
          .filter((conversation) => conversation) // somehow null slips through
          .map((conversationSet) => ({
            ...conversationSet,
            conversation: {
              ...conversationSet.conversation,
              main_thread: conversationSet.conversation.main_thread || conversationSet.thread_id, // fallback thread id
            },
          }));

        return {
          conversations: conversationsAdapter.setAll(initialState, filtered),
          totalPages: total_pages,
        };
      },
      forceRefetch: ({ currentArg, previousArg }) => {
        const isSameCreator = currentArg.creator_id === previousArg?.creator_id;
        const isSamePage = currentArg.page === previousArg?.page;
        return !isSameCreator || !isSamePage;
      },
      serializeQueryArgs: ({ endpointName, queryArgs }) => {
        return `${endpointName}-${queryArgs?.creator_id}`;
      },
      merge: (currentState, incomingState) => {
        conversationsAdapter.addMany(
          currentState.conversations,
          conversationsSelector.selectAll(incomingState.conversations),
        );
      },
    }),
    getSingleConversation: builder.query<IConversationSet, { id: string; creator_id: string }>({
      query: ({ id, creator_id }) => ({
        url: `public/v1/forum/conversation/${id}`,
        params: {
          creator_id,
        },
      }),
      providesTags: [QUERY.singleConversation],
    }),

    createConversation: builder.mutation<ICreateConversationResponse, ICreateConversation>({
      query: (conversation: ICreateConversation) => ({
        url: `public/v1/forum/conversation`,
        method: 'POST',
        body: conversation,
      }),
      invalidatesTags: [QUERY.conversations, QUERY.communityConversations, QUERY.topics],
      async onQueryStarted(conversation, { dispatch, queryFulfilled }) {
        let data: ICreateConversationResponse;
        try {
          ({ data } = await queryFulfilled);
        } catch {
          return;
        }
        const rawCreatorId = cookieService.getUserId();

        const optimisticConversation: IConversationSet = {
          conversation: {
            ...data.conversation,
            order_index: -1000, // manually inject to appear first
          },
          restrictions_list: conversation.restrictions,
          associated_fans: 0,
        };

        // Update topic list (if applicable)
        if (conversation.conversation_type === 'topic') {
          dispatch(
            conversationsApi.util.updateQueryData(
              'getTopicConversations',
              { creator_id: rawCreatorId, page: 1, conversation_type: 'topic' } as any,
              (draft) => {
                topicAdapter.addOne(draft.conversations, optimisticConversation);
              },
            ),
          );
        }

        if (conversation.conversation_type !== 'topic') {
          // Only patch the merged list when the creator cookie is a non-empty string;
          // otherwise an undefined/null id would be serialized into the cache key.
          if (typeof rawCreatorId === 'string' && rawCreatorId.length > 0) {
            // The custom cache key uses creator and type; page 1 is the merged-list convention.
            const conversationsCacheArgs = {
              creator_id: rawCreatorId,
              page: 1,
              conversation_type: conversation.conversation_type,
            } as unknown as IConversationQueryParams;
            dispatch(
              conversationsApi.util.updateQueryData(
                'getConversations',
                conversationsCacheArgs,
                (draft) => {
                  conversationsAdapter.setOne(draft.conversations, optimisticConversation);
                },
              ),
            );
          }
        }
      },
    }),

    updateConversation: builder.mutation<
      IConversation,
      Partial<IConversation> & { params: IConversationQueryParams }
    >({
      query: ({ id, params: _params, access_code, ...patch }) => ({
        url: `public/v1/forum/conversation/${id}`,
        method: 'PUT',
        body: {
          ...patch,
          ...(access_code ? { access_code } : {}),
        },
      }),
      invalidatesTags: [QUERY.conversations, QUERY.singleConversation],
      async onQueryStarted({ id, params, access_code, ...patch }, { dispatch, queryFulfilled }) {
        const optimisticPatch = createSafeOptimisticConversationPatch({
          ...patch,
          ...(access_code ? { access_code } : {}),
        });
        const patchResult = dispatch(
          conversationsApi.util.updateQueryData('getConversations', params, (draft) => {
            const conversationSet = conversationsSelector.selectById(draft.conversations, id);
            conversationsAdapter.updateOne(draft.conversations, {
              id,
              changes: {
                ...conversationSet,
                conversation: {
                  ...conversationSet?.conversation,
                  ...optimisticPatch,
                },
              },
            });
          }),
        );

        // Also optimistically update topic conversations cache (topics list UI)
        const patchTopics = dispatch(
          conversationsApi.util.updateQueryData(
            'getTopicConversations',
            { creator_id: params.creator_id } as any,
            (draft) => {
              const conversationSet = topicSelectors.selectById(draft.conversations, id as string);
              if (conversationSet) {
                topicAdapter.updateOne(draft.conversations, {
                  id: id as string,
                  changes: {
                    ...conversationSet,
                    conversation: {
                      ...conversationSet.conversation,
                      ...optimisticPatch,
                      // Carefully merge miscellaneous to preserve existing keys
                      ...(optimisticPatch.miscellaneous
                        ? {
                            miscellaneous: {
                              ...conversationSet.conversation.miscellaneous,
                              ...optimisticPatch.miscellaneous,
                            },
                          }
                        : {}),
                    },
                  },
                });
              }
            },
          ),
        );

        try {
          await queryFulfilled;
        } catch {
          patchResult.undo();
          patchTopics.undo();
        }
      },
    }),
    deleteConversation: builder.mutation<
      { success: boolean; id: string },
      { id: string; conversation_type: IConversation['conversation_type'] }
    >({
      query({ id }) {
        return {
          url: `public/v1/forum/conversation/${id}`,
          method: 'DELETE',
        };
      },
      invalidatesTags: [QUERY.conversations],
      async onQueryStarted({ id, conversation_type }, { dispatch, queryFulfilled }) {
        const creator_id = cookieService.getUserId();
        // The custom cache key uses creator and type; page 1 is the merged-list convention.
        // Only patch with a real creator cookie: a missing or non-string value would be
        // serialized into a fabricated cache key that no real query ever matches.
        const patchResult =
          typeof creator_id === 'string' && creator_id.length > 0
            ? dispatch(
                conversationsApi.util.updateQueryData(
                  'getConversations',
                  {
                    creator_id,
                    conversation_type,
                    page: 1,
                  } as unknown as IConversationQueryParams,
                  (draft) => {
                    conversationsAdapter.removeOne(draft.conversations, id);
                  },
                ),
              )
            : undefined;

        const patchTopic = dispatch(
          conversationsApi.util.updateQueryData(
            'getTopicConversations',
            { creator_id, conversation_type: 'topic' } as any,
            (draft) => {
              conversationsAdapter.removeOne(draft.conversations, id);
            },
          ),
        );

        try {
          await queryFulfilled;
        } catch {
          patchResult?.undo();
          patchTopic.undo();
        }
      },
    }),
    updateConversationRestrictions: builder.mutation<
      IConversationRestrictionOptions,
      {
        conversationId: string;
        restrictions: IConversationRestrictionOptions;
        conversation_type: IConversationQueryParams['conversation_type'];
      }
    >({
      query: ({ conversationId, restrictions, conversation_type: _conversationType }) => ({
        url: `public/v1/forum/update_restrictions/${conversationId}`,
        method: 'PUT',
        body: {
          restrictions: buildModeratorRestrictionsPayload(
            restrictions,
            restrictions.moderators_ids || [],
          ),
        },
      }),
      invalidatesTags: [QUERY.conversations, QUERY.singleConversation],
      async onQueryStarted(
        { conversationId, restrictions, conversation_type },
        { dispatch, queryFulfilled },
      ) {
        const rawCreatorId = cookieService.getUserId();
        const patchConversationListResult =
          typeof rawCreatorId === 'string' && rawCreatorId.length > 0
            ? dispatch(
                conversationsApi.util.updateQueryData(
                  'getConversations',
                  {
                    creator_id: rawCreatorId,
                    conversation_type,
                    page: 1,
                    items_per_page: 10,
                  },
                  (draft) => {
                    const conversationSet = conversationsSelector.selectById(
                      draft.conversations,
                      conversationId,
                    );
                    if (conversationSet) {
                      conversationsAdapter.updateOne(draft.conversations, {
                        id: conversationId,
                        changes: {
                          ...conversationSet,
                          restrictions_list: restrictions,
                        },
                      });
                    }
                  },
                ),
              )
            : undefined;

        const patchCommunityListResult = dispatch(
          conversationsApi.util.updateQueryData(
            'getCommunityConversations',
            {
              creator_id: cookieService.getUserId(),
            } as any,
            (draft) => {
              const conversationSet = conversationsSelector.selectById(
                draft.conversations,
                conversationId,
              );
              if (conversationSet) {
                conversationsAdapter.updateOne(draft.conversations, {
                  id: conversationId,
                  changes: {
                    ...conversationSet,
                    restrictions_list: restrictions,
                  },
                });
              }
            },
          ),
        );

        try {
          await queryFulfilled;
        } catch {
          patchConversationListResult?.undo();
          patchCommunityListResult.undo();
        }
      },
    }),
    getTheConversationFans: builder.query<INormalizedFansResponse, IConversationFansQueryParams>({
      query: ({ id, ...params }) => ({
        url: `public/v1/forum/fans_of_conversation/${id}`,
        params,
      }),
      providesTags: [QUERY.conversations],
      transformResponse: ({ fans, total_pages, total_records, current_page }) => {
        const initialState = conversationFansAdapter.getInitialState();

        return {
          fans: conversationFansAdapter.setAll(initialState, fans),
          totalPages: total_pages,
          totalRecords: total_records,
          currentPage: current_page,
        };
      },
      forceRefetch: ({ currentArg, previousArg }) => {
        const isSameConversation = currentArg.id === previousArg?.id;
        const isSamePage = currentArg.page === previousArg?.page;
        const isSameSearch = currentArg.search === previousArg?.search;
        return !isSameConversation || !isSamePage || !isSameSearch;
      },
      serializeQueryArgs: ({ endpointName, queryArgs }) => {
        return `${endpointName}-${queryArgs?.id}-${queryArgs?.search || ''}`;
      },
      merge: (currentState, incomingState, { arg }) => {
        if (arg.page === 1) {
          return incomingState;
        }
        conversationFansAdapter.addMany(
          currentState.fans,
          conversationFansSelector.selectAll(incomingState.fans),
        );
      },
    }),
    getConversationFans: builder.query<
      { fans: any; total_pages: number },
      { id: string; page: number; items_per_page: number; search?: string; creator_id?: string }
    >({
      // The API only includes fan emails when the request carries the
      // conversation's creator_id; without it the list has display fields only.
      query: ({ id, page, items_per_page, search, creator_id }) => ({
        url: `public/v1/forum/fans_of_conversation/${id}`,
        params: {
          page,
          items_per_page,
          search,
          creator_id,
        },
      }),
      providesTags: [QUERY.conversationFans],
      transformResponse: (response: { fans: IFansOfConverstion[]; total_pages: number }) => {
        const initialState = conversationFansAdapter.getInitialState();
        return {
          fans: conversationFansAdapter.setAll(initialState, response.fans),
          total_pages: response.total_pages,
        };
      },
    }),
    markCreatorConversation: builder.mutation<
      { message: string },
      MarkCreatorConversationActionRequest & {
        conversation_type?: IConversationQueryParams['conversation_type'];
      }
    >({
      query: ({ conversation_type: _conversationType, ...request }) =>
        buildMarkCreatorConversationApiRequest(request),
      invalidatesTags: [QUERY.conversations, QUERY.singleConversation],
      async onQueryStarted(
        { conversation_id, creator_id, conversation_type },
        { dispatch, queryFulfilled },
      ) {
        const getConversationsCacheArgs: IConversationQueryParams = {
          creator_id,
          conversation_type: conversation_type ?? 'bidirectional',
          page: 1,
          items_per_page: 10,
        };
        const patchConversationList = dispatch(
          conversationsApi.util.updateQueryData(
            'getConversations',
            getConversationsCacheArgs,
            (draft) => {
              const conversationSet = conversationsSelector.selectById(
                draft.conversations,
                conversation_id,
              );
              if (conversationSet) {
                conversationsAdapter.updateOne(draft.conversations, {
                  id: conversation_id,
                  changes: {
                    ...conversationSet,
                    unread_messages_count: 0,
                    conversation: {
                      ...conversationSet.conversation,
                      opened_by_creator: true,
                    },
                  },
                });
              }
            },
          ),
        );

        const patchSingleConversation = dispatch(
          conversationsApi.util.updateQueryData(
            'getSingleConversation',
            { id: conversation_id, creator_id },
            (draft) => {
              if (!draft) return;
              draft.unread_messages_count = 0;
              draft.conversation = {
                ...draft.conversation,
                opened_by_creator: true,
              };
            },
          ),
        );

        try {
          await queryFulfilled;
        } catch {
          patchConversationList.undo();
          patchSingleConversation.undo();
        }
      },
    }),
    updateConversationParticipants: builder.mutation<
      { message: string; removed_fans?: string[] },
      { conversation_id: string; fan_ids: string[]; creator_id: string; update: 'add' | 'remove' }
    >({
      query: ({ conversation_id, fan_ids, creator_id, update }) => ({
        url: `public/v1/forum/update_conversation_participants/${conversation_id}`,
        method: 'PUT',
        body: { fan_ids, creator_id, update },
      }),
      invalidatesTags: [QUERY.conversations, QUERY.singleConversation, QUERY.conversationFans],
      async onQueryStarted({ conversation_id, fan_ids, update }, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(conversationsApi.util.resetApiState());
        } catch (error) {
          console.error('Failed to update conversation participants:', error);
        }
      },
    }),

    generateCommunityAccessCode: builder.mutation<{ access_code: string }, string>({
      query: (communityId) => ({
        url: `public/v1/forum/communities/${communityId}/generate_code`,
        method: 'POST',
      }),
      invalidatesTags: [QUERY.conversations, QUERY.singleConversation],
    }),

    reorderConversations: builder.mutation<{ message: string }, IReorderConversationsPayload>({
      query: ({ creator_id, conversation_type, ordered_ids }) => ({
        url: `public/v1/forum/update_conversation_order_index/${creator_id}`,
        method: 'PUT',
        body: {
          conversation_type,
          ordered_ids,
        },
      }),
      invalidatesTags: [QUERY.conversations, QUERY.topics],
      async onQueryStarted(
        { creator_id, conversation_type, ordered_ids },
        { dispatch, queryFulfilled },
      ) {
        if (conversation_type !== 'topic') return;

        const patchResult = dispatch(
          conversationsApi.util.updateQueryData(
            'getTopicConversations',
            { creator_id, page: 1, conversation_type: 'topic' } as any,
            (draft) => {
              const current = topicAdapter.getSelectors().selectAll(draft.conversations);

              // Map current list into a record by ID for quick access
              const mapById = new Map(current.map((conv) => [conv.conversation.id, conv]));

              // Build new ordered list
              const reordered = ordered_ids
                .map((id, index) => {
                  const item = mapById.get(id);
                  if (!item) return null;
                  return {
                    ...item,
                    conversation: {
                      ...item.conversation,
                      order_index: index,
                    },
                  };
                })
                .filter(Boolean) as typeof current;

              // Set all reordered items
              topicAdapter.setAll(draft.conversations, reordered);
            },
          ),
        );

        try {
          await queryFulfilled;
        } catch (err) {
          patchResult.undo(); // rollback in case of failure
        }
      },
    }),

    findDirectMessage: builder.query({
      query: (params) => ({
        url: `public/v1/forum/find_direct_message/${params.fan_id}`,
        params: {
          creator_id: params.creator_id,
        },
      }),
      providesTags: [QUERY.conversations],
    }),
  }),
});

export const {
  useGetConversationsQuery,
  useGetSingleConversationQuery,
  useCreateConversationMutation,
  useUpdateConversationMutation,
  useDeleteConversationMutation,
  useGetCommunityConversationsQuery,
  useLazyGetCommunityConversationsQuery,
  useUpdateConversationRestrictionsMutation,
  useLazyGetTheConversationFansQuery,
  useGetConversationFansQuery,
  useMarkCreatorConversationMutation,
  useUpdateConversationParticipantsMutation,
  useLazyFindDirectMessageQuery,
  useGetTopicConversationsQuery,
  useReorderConversationsMutation,
  useGenerateCommunityAccessCodeMutation,
} = conversationsApi;
